import React from 'react';
import { X, Package, DollarSign, Calendar, User, CreditCard, MapPin, Zap, Info } from 'lucide-react';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import type { SlotWithReservation } from '../types';

interface SlotDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  slot: SlotWithReservation | null;
}

const SlotDetailsModal: React.FC<SlotDetailsModalProps> = ({
  isOpen,
  onClose,
  slot
}) => {
  if (!isOpen || !slot) return null;

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      confirmed: { label: 'Confirmed', className: 'bg-moonbone text-ink' },
      pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
      cancelled: { label: 'Cancelled', className: 'bg-gray-100 text-gray-800' }
    };
    const config = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const getPaymentBadge = (paymentStatus: string) => {
    const statusMap: Record<string, { label: string; className: string }> = {
      paid: { label: 'Paid', className: 'bg-green-100 text-green-800' },
      pending: { label: 'Pending Payment', className: 'bg-yellow-100 text-yellow-800' },
      refunded: { label: 'Refunded', className: 'bg-gray-100 text-gray-800' }
    };
    const config = statusMap[paymentStatus] || { label: paymentStatus, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Package className="w-6 h-6 text-secondary" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Slot {slot.slot_number}
              </h2>
              <p className="text-sm text-gray-500">
                {slot.is_reserved ? 'Reserved Booth' : 'Available Booth'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Slot Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-secondary" />
              Booth Information
            </h3>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Slot Number</div>
                  <div className="text-sm font-medium text-gray-900">{slot.slot_number}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Slot Type</div>
                  <div className="text-sm font-medium text-gray-900 capitalize">{slot.slot_type}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Dimensions</div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-gray-400" />
                    {slot.dimensions || 'Not specified'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Base Price (per day)</div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <DollarSign className="w-3 h-3 text-gray-400" />
                    {formatCurrency(slot.base_price).replace('$', '')} {/* Issue #905: Decimal.js */}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 pt-2 border-t border-gray-200">
                <div className="flex items-center gap-2">
                  <Zap className={`w-4 h-4 ${slot.has_power ? 'text-secondary' : 'text-gray-300'}`} />
                  <span className="text-sm text-gray-700">
                    {slot.has_power ? 'Electrical power available' : 'No electrical power'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${slot.is_available ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className="text-sm text-gray-700">
                    {slot.is_available ? 'Available for booking' : 'Not available'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Reservation Information */}
          {slot.is_reserved && slot.reservation ? (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-secondary" />
                Reservation Details
              </h3>

              <div className="bg-moonbone rounded-lg p-4 space-y-4">
                {/* Status and Payment */}
                <div className="flex items-center gap-3 pb-3 border-b border-teal-100">
                  {getStatusBadge(slot.reservation.status)}
                  {getPaymentBadge(slot.reservation.payment_status)}
                </div>

                {/* Vendor Information */}
                <div>
                  <div className="text-xs text-secondary font-medium mb-2 flex items-center gap-1">
                    <User className="w-4 h-4" />
                    Vendor Information
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="text-xs text-gray-500">Business Name</div>
                      <div className="text-sm font-medium text-gray-900">{slot.reservation.vendor_name}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">Email</div>
                        <a
                          href={`mailto:${slot.reservation.vendor_email}`}
                          className="text-sm text-secondary hover:text-primary-dark hover:underline"
                        >
                          {slot.reservation.vendor_email}
                        </a>
                      </div>
                      {slot.reservation.vendor_phone && (
                        <div>
                          <div className="text-xs text-gray-500">Phone</div>
                          <a
                            href={`tel:${slot.reservation.vendor_phone}`}
                            className="text-sm text-secondary hover:text-primary-dark hover:underline"
                          >
                            {slot.reservation.vendor_phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Reservation Details */}
                <div>
                  <div className="text-xs text-secondary font-medium mb-2 flex items-center gap-1">
                    <CreditCard className="w-4 h-4" />
                    Reservation Details
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">Reservation Type</div>
                      <div className="text-sm font-medium text-gray-900 capitalize">
                        {slot.reservation.reservation_type.replace('_', ' ')}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">Price Paid</div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(slot.reservation.price_paid)} {/* Issue #905: Decimal.js */}
                      </div>
                    </div>
                  </div>

                  {/* Selected Dates for Per-Day Reservations */}
                  {slot.reservation.reservation_type === 'per_day' && slot.reservation.selected_dates && (
                    <div className="mt-3">
                      <div className="text-xs text-gray-500 mb-1">Reserved Dates</div>
                      <div className="flex flex-wrap gap-2">
                        {slot.reservation.selected_dates.map((date: string, index: number) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs bg-white text-gray-700 rounded border border-teal-200"
                          >
                            {new Date(date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Created At */}
                  <div className="mt-3">
                    <div className="text-xs text-gray-500">Booked On</div>
                    <div className="text-sm text-gray-900">
                      {formatDate(slot.reservation.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Package className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-green-900 mb-1">
                    This booth is available
                  </h4>
                  <p className="text-sm text-green-700">
                    No active reservation for this slot. Vendors can book this booth through the
                    reservation system.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SlotDetailsModal;
